import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from '../../api/client'

/**
 * Backup-ul pe aplicatie - `v4/api/src/Controller/BusinessAppsBackupController.php`.
 *
 *   GET  /business-apps/backup-status          starea pe scurt a fiecarei aplicatii
 *   GET  /business-apps/{id}/backup            cardul de pe fisa, pe componente
 *   POST /business-apps/{id}/backup/threshold  pragul aplicatiei (`hours`, gol = implicit)
 */

/** `overdue` = peste prag; `unknown` = nicio sursa nu spune nimic (NU "lipseste"). */
export type BackupState = 'ok' | 'warning' | 'overdue' | 'unknown' | 'none'

export type BackupSourceLine = {
  kind: 'agent' | 'protectee' | 'database'
  label: string
  lastOk: string | null
  ageHours: number | null
  never: boolean
  failed: boolean
  result: string
}

export type BackupComponent = {
  deviceId: number | null
  ciId: number | null
  name: string
  role: number
  roleLabel: string
  /** Baza de date si serverul de aplicatie trebuie sa aiba copie. */
  required: boolean
  state: Exclude<BackupState, 'none'>
  stateLabel: string
  lastOk: string | null
  ageHours: number | null
  source: string | null
  sources: BackupSourceLine[]
  message: string
}

export type AppBackupSummary = {
  appId: number
  name: string
  criticality: number
  thresholdHours: number
  thresholdLabel: string
  thresholdDefault: boolean
  defaultHours: number
  state: BackupState
  label: string
  signals: string[]
  unknownServers: string[]
}

export type AppBackupDetail = AppBackupSummary & { components: BackupComponent[]; canEdit: boolean }

export type BackupStatusPage = { ready: boolean; apps: Record<string, AppBackupSummary> }

export function backupStatusQuery() {
  return queryOptions({
    queryKey: ['business-apps', 'backup', 'status'],
    queryFn: () => apiGet<BackupStatusPage>('/business-apps/backup-status'),
    retry: false,
  })
}

export function appBackupQuery(id: number) {
  return queryOptions({
    queryKey: ['business-apps', 'backup', 'detail', id],
    queryFn: () => apiGet<AppBackupDetail>(`/business-apps/${id}/backup`),
    retry: false,
  })
}

export function saveBackupThreshold(id: number, hours: string) {
  return apiPost<{ status: 'OK' | 'NOK'; message: string }>(`/business-apps/${id}/backup/threshold`, { hours })
}

/** Raspunsul chiar e al backup-ului (nu altceva intors pe aceeasi cale de un server mai vechi). */
export function isBackupDetail(data: unknown): data is AppBackupDetail {
  const d = data as Partial<AppBackupDetail> | null | undefined
  return d != null && typeof d.thresholdHours === 'number' && typeof d.state === 'string' && Array.isArray(d.components)
}

/** Starea unei aplicatii din `backup-status`, daca raspunsul are forma asteptata. */
export function backupOf(data: BackupStatusPage | undefined, appId: number): AppBackupSummary | null {
  if (!data || data.ready !== true || data.apps == null || typeof data.apps !== 'object' || Array.isArray(data.apps)) return null
  const b = data.apps[String(appId)]
  return b && typeof b.state === 'string' ? b : null
}
